import { DialogProps } from '@tableau/widgets'; // Only importing the type so that the library is not statically loaded. Static loading of this library does not work on desktop because it is loaded too early for some dependencies.
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { AddInInstancePresModel } from '../../gen-src/preslayer/PresModels';
import { ApiDispatcher } from '../common-bootstrap/ApiDispatcher';
import { Dialog } from './Dialog';

export class DialogElement implements Dialog {
  private activeDialogElement: HTMLElement | null = null;
  private iFrameElement: HTMLElement | null = null;
  private _isCreated = false;

  public constructor(
    private _width: string,
    private _height: string,
    private _url: string,
    private _isModal: boolean,
    private _extensionInstance: AddInInstancePresModel,
    private handleUserCloseEvent: () => void,
  ) {}

  public get isOpen(): boolean {
    return this.activeDialogElement !== null;
  }

  public get isCreated(): boolean {
    return this._isCreated;
  }

  public get width(): string {
    return this._width;
  }

  public get height(): string {
    return this._height;
  }

  public get url(): string {
    return this._url;
  }

  public async createDialog(): Promise<void> {
    if (this._isCreated) {
      return;
    }

    const widgets = await import('@tableau/widgets');

    const w = Number.parseInt(this._width, 10);
    const h = Number.parseInt(this._height, 10);
    const iframeStyle: React.CSSProperties = {
      width: w,
      height: h,
      border: 'none',
      margin: 0,
      padding: 0,
      overflow: 'hidden',
    };
    const popupContents = React.createElement('iframe', {
      src: this._url,
      id: this._extensionInstance.addInRegistrationPresModel.addInId,
      style: iframeStyle,
    });
    const dialogWidget = React.createElement(widgets.DialogWidget, {
      testId: 'configure-dialog',
      children: popupContents,
      dialogBodyStyle: { padding: 0 },
      isModal: this._isModal,
      type: widgets.DialogType.NormalWithTitleBar,
      showCloseButton: true,
      showFooter: false,
      title: this._extensionInstance.addInRegistrationPresModel.addInName,
      positioningBehavior: widgets.DialogPositioningBehavior.Draggable,
      onCloseRequest: this.handleUserCloseEvent,
    });
    const dialogComponent = ReactDOM.render<DialogProps>(dialogWidget, this.createActiveDialogContainer());
    this.iFrameElement = document.getElementById(this._extensionInstance.addInRegistrationPresModel.addInId);

    // We need to ensure that the components are found and rendering on server
    // if not log the error and return.
    if (typeof dialogComponent === undefined || this.iFrameElement === null) {
      console.error('Unable to initialize configure dialog.');
      return;
    }

    this._isCreated = true;
  }

  public closeDialog(): void {
    if (!this.isOpen) {
      return;
    }
    ReactDOM.unmountComponentAtNode(this.activeDialogElement!);
    document.body.removeChild(this.activeDialogElement!);
    this.activeDialogElement = null;
    this.iFrameElement = null;
  }

  public cleanupDialog(): void {
    if (this.isOpen) {
      this.closeDialog();
    }
    this._isCreated = false;
  }

  public showDialog(dialogDispatcher: ApiDispatcher): void {
    if (!this._isCreated) {
      return;
    }
    dialogDispatcher.setExtensionFrame(this.iFrameElement as HTMLIFrameElement);
  }

  private createActiveDialogContainer(): HTMLElement {
    this.activeDialogElement = document.createElement('div');
    this.activeDialogElement.id = this._extensionInstance.addInRegistrationPresModel.addInId + '-div';
    document.body.appendChild(this.activeDialogElement);
    return this.activeDialogElement;
  }
}
